// =============================================
// docStore — IndexedDB blob store for DMS documents
// =============================================
// Document bytes (gzip-compressed, base64) are far too large to keep inline in
// the database row at scale (hundreds of thousands of files would bloat
// dms_documents into tens of GB and slow every query + backup). So the bytes
// live OUT of the row, in object storage, and the row keeps only a tiny pointer
// in `dms_documents.file_path`:
//
//   • dev / test  → this IndexedDB store, key  `idb://<key>`
//   • production  → Cloud Storage, key/URL     `https://storage.../<key>`
//
// This mirrors `src/lib/audioStore.ts` (the music blob store). Only this module
// (and the key format) changes between dev and prod; everything above it reads
// bytes via `file_path` and is unaware of where they physically sit.
//
// We store the SAME gzip-base64 string that lived in `file_base64`, so the
// existing `decompressBase64ToBytes` read path is unchanged — the only
// difference is WHERE the string comes from (out-of-row blob store vs the DB).
// =============================================

const DB_NAME = 'akt_doc_blobs';
const STORE = 'docs';
const DB_VERSION = 1;

const IDB_PREFIX = 'idb://';

let _dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

/** Build a fresh storage key. Caller stores `key` in `dms_documents.file_path`. */
export function newDocKey(): string {
  const rand = Math.random().toString(36).slice(2, 10);
  return IDB_PREFIX + Date.now().toString(36) + '-' + rand;
}

function bareKey(key: string): string {
  return key.startsWith(IDB_PREFIX) ? key.slice(IDB_PREFIX.length) : key;
}

/** Is this a key WE own (local IndexedDB), vs a real remote URL (Cloud Storage on live)? */
export function isIdbKey(key: string | null | undefined): boolean {
  return !!key && key.startsWith(IDB_PREFIX);
}

/** Store a document's gzip-base64 string under `key`. */
export async function putDocBase64(key: string, base64: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(base64, bareKey(key));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/** Fetch a document's gzip-base64 string for `key`, or null if missing. */
export async function getDocBase64(key: string): Promise<string | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(bareKey(key));
    req.onsuccess = () => resolve((req.result as string) ?? null);
    req.onerror = () => reject(req.error);
  });
}

/** True if a blob is present for `key`. */
export async function hasDoc(key: string): Promise<boolean> {
  if (!isIdbKey(key)) return false;
  const v = await getDocBase64(key);
  return v != null;
}

/** Remove a document blob (best-effort — never block a delete on this). */
export async function deleteDoc(key: string): Promise<void> {
  if (!isIdbKey(key)) return;
  const db = await openDb();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(bareKey(key));
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

/**
 * Approximate local blob-store usage, when the Storage API is available.
 * Returns { usageBytes, quotaBytes } or null. Informational only.
 */
export async function estimateUsage(): Promise<{ usageBytes: number; quotaBytes: number } | null> {
  try {
    const anyNav = navigator as any;
    if (anyNav.storage && anyNav.storage.estimate) {
      const e = await anyNav.storage.estimate();
      return { usageBytes: e.usage || 0, quotaBytes: e.quota || 0 };
    }
  } catch { /* ignore */ }
  return null;
}
